/**
 * Error Boundary component.
 * Catches JavaScript errors in child components and reports them to the observability provider.
 */

import { Component, useEffect, type ReactNode } from "react";
import { getProvider } from "../lib/observability";

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Scope tag identifying this boundary (e.g. "dashboard.live-options"). */
  section?: string;
}

export interface TabFallbackProps {
  /** Human-readable section label shown in the fallback UI. */
  section?: string;
  resetError?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Inline fallback for section-level boundaries (tabs, panels).
 * Resets only the failed subtree — does not reload the page.
 */
export function TabFallback({ section, resetError }: TabFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center rounded-lg border border-border/50 bg-secondary/50 min-h-[200px]">
      <p className="text-sm font-semibold text-destructive">
        {section ? `${section} failed to load` : "This section failed to load"}
      </p>
      <p className="text-xs text-muted-foreground">
        The error has been reported. Try refreshing this section.
      </p>
      {resetError && (
        <button
          onClick={resetError}
          className="px-4 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Error Boundary component that catches errors and reports to the observability provider.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary section="dashboard.live-options" fallback={<TabFallback section="Live Options" />}>
 *   <LiveOptionsTab />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { section = "error-boundary" } = this.props;
    const provider = getProvider();
    provider.captureException(error, {
      section,
      componentStack: errorInfo.componentStack,
      message: error.message,
    });

    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error);
      console.error("Component stack:", errorInfo.componentStack);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback as ReactNode;
      }
      return <TabFallback section={this.props.section} resetError={this.resetError} />;
    }
    return this.props.children;
  }
}

/**
 * Hook to add a breadcrumb when a component mounts (for debugging context).
 */
export function useErrorBoundaryBreadcrumb(section: string) {
  useEffect(() => {
    getProvider().addBreadcrumb("ErrorBoundary mounted", "ui", { section });
  }, [section]);
}

/**
 * Higher-order component to wrap components with ErrorBoundary.
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options?: { fallback?: ReactNode; section?: string }
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={options?.fallback} section={options?.section}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

// Barrel export
export default ErrorBoundary;
